import { Tag } from '@lastbrand/web-ui/tag'
import { css } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'

import type { ProductCardImage } from '@src/components/product-card/product-card.types'
import { getTagPriority } from '@src/components/product-card/product-image-section/product-tags/product-tags.helper'
import type { ProductTileImageAsset } from '@src/ds/types/product-tile'
import { Image } from '@src/quince-components/src'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import { PRODUCT_TAG_TYPE } from '@src/quince-site-common/src/domains/ProductTag'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { PRODUCT_TAGS_IMAGE_LIMIT } from '@src/quince-site-common/src/utils/constants'
import { getDisplayKey } from '@src/utils/i18n.utils'

import { CardImageCarousel } from './card-image-carousel'

import type { ImageTileProps } from './image-tile.types'

const DEFAULT_IMAGE_MAX_WIDTH = 800

const HERO_MEDIA_ASPECT_RATIO = '1 / 1.25'

const tagTypeToDSTagMap = {
  // Legacy SECONDARY promotional badges (e.g. "Flat_99") render a solid $cantalope (#ffa273) pill
  // (product-tags.module.scss `.tag__SECONDARY`, opacity 1). The web-ui `cantaloupe` variant maps
  // to cantaloupe-80 — the same token the DS PDP tag path uses (product-tags.tsx dsTagSecondaryCss).
  // eslint-disable-next-line i18next/no-literal-string -- DS Tag variant token, not translatable copy
  [PRODUCT_TAG_TYPE.SECONDARY]: 'cantaloupe' as const,
  // eslint-disable-next-line i18next/no-literal-string -- DS Tag variant token, not translatable copy
  [PRODUCT_TAG_TYPE.PRIMARY]: 'transparent' as const,
} as const

const DEFAULT_DS_TAG_VARIANT: (typeof tagTypeToDSTagMap)[PRODUCT_TAG_TYPE.PRIMARY] =
  tagTypeToDSTagMap[PRODUCT_TAG_TYPE.PRIMARY]

function switchFirstImage(images: ProductCardImage[]): ProductCardImage[] {
  const [firstImage, secondImage] = images
  if (!firstImage) return []
  if (!secondImage) return [firstImage]
  return [secondImage, firstImage]
}

function toHeroImages(images: ProductCardImage[]): ProductTileImageAsset[] {
  return images.map(img => ({
    id: img.id,
    url: img.url,
    alt: img.alt,
    height: img.height,
    width: img.width,
    description: img.description || undefined,
  }))
}

/** Semi-transparent white behind product image tags (matches Figma / custom-white-trans-70). */
const TAG_BG_CSS = { bg: 'white-trans-70', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)' } as const

/**
 * Data-slot regions on a product tile that scope its hover / focus-visible
 * state. Hovering or moving focus into ANY of these regions activates the
 * tile's hover effects (image swap via `productTileImageHoverSwapCss`,
 * add-to-bag reveal via `addToCartButtonHoverRevealParentCss`). Hovering
 * other tile children (brand name, rating, color swatches, delivery message)
 * intentionally does NOT trigger hover state.
 */
export const PRODUCT_TILE_HOVER_SLOTS = [
  'product-tile-hover-image-area',
  'product-tile-hover-title-area',
  'product-tile-hover-price-area',
] as const

// Every clause is chained with `:has([data-slot="product-tile-image-secondary"])`
// so the opacity swap ONLY fires when a secondary image layer is actually in
// the DOM. Products with a single image (no `secondImage.url`) don't render
// the secondary slot — without this guard, hovering them would fade the
// primary to `opacity: 0` and leave a blank tile (broken-image alt text and
// all), since there's nothing to swap to.
export const productTileImageHoverSwapCss = css.raw({
  '@media (hover: hover)': {
    '&:has([data-slot="product-tile-image-secondary"]):has([data-slot="product-tile-hover-image-area"]:hover), &:has([data-slot="product-tile-image-secondary"]):has([data-slot="product-tile-hover-image-area"] :focus-visible), &:has([data-slot="product-tile-image-secondary"]):has([data-slot="product-tile-hover-title-area"]:hover), &:has([data-slot="product-tile-image-secondary"]):has([data-slot="product-tile-hover-title-area"] :focus-visible), &:has([data-slot="product-tile-image-secondary"]):has([data-slot="product-tile-hover-price-area"]:hover), &:has([data-slot="product-tile-image-secondary"]):has([data-slot="product-tile-hover-price-area"] :focus-visible)':
      {
        '& [data-slot="product-tile-image-primary"]': {
          opacity: 0,
        },
        '& [data-slot="product-tile-image-secondary"]': {
          opacity: 1,
        },
      },
  },
})

export function ImageTile({
  images: rawImages,
  variantFallbackAlt,
  applicableTags,
  readyToShipEnabled = false,
  showSecondImageOnProductTiles,
  imageLoading,
  imageQuality,
  imageMaxWidth = DEFAULT_IMAGE_MAX_WIDTH,
  tileVariant = 'default',
  forceShowSecondImage,
  enableImageCarousel = false,
  renderSlideLink,
  onCarouselSlideView,
}: ImageTileProps) {
  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const isNewLocalisedFlow = useI18nPreviewContext()
  const { isB2BMode } = useStorefrontModeContext()

  const heroImages = useMemo<ProductTileImageAsset[]>(() => {
    const imgs = showSecondImageOnProductTiles ? switchFirstImage(rawImages) : rawImages
    return toHeroImages(imgs).slice(0, 2)
  }, [rawImages, showSecondImageOnProductTiles])

  const firstImage = heroImages[0]
  const secondImage = heroImages[1]
  // Hover swap is desktop-only: mobile has no real hover; single-image variants
  // have nothing to swap to.
  const hasSecondImage = !isMobile && Boolean(secondImage) && secondImage !== firstImage

  const resolveAlt = (img: ProductTileImageAsset | undefined): string => {
    const trimmedAlt = img?.alt.trim() ?? ''
    const trimmedDescription = img?.description?.trim() ?? ''
    const trimmedFallbackAlt = (variantFallbackAlt ?? '').trim()
    if (trimmedAlt !== '') return trimmedAlt
    if (trimmedDescription !== '') return trimmedDescription
    if (trimmedFallbackAlt !== '') return trimmedFallbackAlt
    return String(t('product_image'))
  }

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'

  const imageTags = useMemo(() => {
    if (isMini) return undefined
    const showReadyToShip = Boolean(readyToShipEnabled)
    if (!applicableTags.length && !showReadyToShip) return undefined

    const sortedTags = [...applicableTags]
      .sort((a, b) => getTagPriority(a.value.toLowerCase()) - getTagPriority(b.value.toLowerCase()))
      .slice(0, PRODUCT_TAGS_IMAGE_LIMIT)

    const useLocalizedDisplayKey = isNewLocalisedFlow || isB2BMode

    return (
      <>
        {sortedTags.map(tag => {
          const variant = tag.tagType != null ? tagTypeToDSTagMap[tag.tagType] : DEFAULT_DS_TAG_VARIANT
          const text = productHelper.getTagValueWithFirstLetterUpperCase(
            getDisplayKey(tag, 'displayName', tag.value, useLocalizedDisplayKey),
          )

          // SECONDARY promo badges bring their own solid background — keyed off the tag type,
          // not the variant colour, so a future remap of SECONDARY keeps skipping the overlay.
          const tagBgCss = tag.tagType === PRODUCT_TAG_TYPE.SECONDARY ? undefined : TAG_BG_CSS
          return (
            <Tag key={tag.value} variant={variant} css={tagBgCss}>
              {text}
            </Tag>
          )
        })}
        {showReadyToShip ? (
          <Tag key="ready-to-ship" variant="offwhite" css={TAG_BG_CSS}>
            {t('ready_to_ship')}
          </Tag>
        ) : null}
      </>
    )
  }, [applicableTags, isB2BMode, isMini, isNewLocalisedFlow, readyToShipEnabled, t])
  const fillImageClass = css({
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
  })
  // Default opacities live on the layer classes so that the parent hover CSS
  // rule (`productTileImageHoverSwapCss`) can override them when a hover-trigger region is active.
  // When `forceShowSecondImage` is provided, we set opacity inline as an
  // override (inline style beats class), e.g. for tests or programmatic preview.
  const primaryLayerClass = css({ position: 'absolute', inset: 0, opacity: 1 })
  const secondaryLayerClass = css({ position: 'absolute', inset: 0, opacity: 0 })
  const forcedPrimaryStyle =
    forceShowSecondImage === true && hasSecondImage
      ? { opacity: 0 }
      : forceShowSecondImage === false
        ? { opacity: 1 }
        : undefined
  const forcedSecondaryStyle =
    forceShowSecondImage === true && hasSecondImage
      ? { opacity: 1 }
      : forceShowSecondImage === false
        ? { opacity: 0 }
        : undefined
  // Always render the primary image layer — even when `firstImage` is
  // undefined or its URL is empty. This matches legacy `ProductImageSection`,
  // which always mounts `<Image>` and lets the browser paint the alt text on
  // the broken-image surface (`<img alt="…" src="">` → `.imageError` shows
  // alt). Gating on `firstImage` here was producing an empty image area in
  // the DOM for products with no images, with no alt visible at all.
  // `resolveAlt(undefined)` falls back to `variantFallbackAlt` (e.g. the
  // selected color + product title) or `t('product_image')`.
  const heroImageNode = (
    <div className={css({ position: 'absolute', inset: 0, overflow: 'hidden' })}>
      <div data-slot="product-tile-image-primary" className={primaryLayerClass} style={forcedPrimaryStyle}>
        <Image
          key="product-card-image-primary"
          url={firstImage?.url ?? ''}
          alt={resolveAlt(firstImage)}
          title=""
          width={firstImage?.width}
          height={firstImage?.height}
          loading={imageLoading}
          quality={imageQuality}
          maxWidth={imageMaxWidth}
          className={fillImageClass}
        />
      </div>
      {hasSecondImage && secondImage?.url ? (
        <div data-slot="product-tile-image-secondary" className={secondaryLayerClass} style={forcedSecondaryStyle}>
          <Image
            key="product-card-image-secondary"
            url={secondImage.url}
            alt={resolveAlt(secondImage)}
            title=""
            width={secondImage.width}
            height={secondImage.height}
            loading={imageLoading}
            quality={imageQuality}
            maxWidth={imageMaxWidth}
            className={fillImageClass}
          />
        </div>
      ) : null}
    </div>
  )

  // Statsig-gated, mobile-only: swap the single hero image for a swipeable carousel of the full
  // per-variant image list. Control / desktop / single-image tiles fall through to `heroImageNode`
  // unchanged, so there is no visual or layout difference outside the treatment.
  const showImageCarousel = enableImageCarousel && isMobile && rawImages.length > 1
  const trimmedFallbackAlt = variantFallbackAlt?.trim()
  const carouselAriaLabel = trimmedFallbackAlt ? trimmedFallbackAlt : String(t('product_image'))
  const imageNode =
    showImageCarousel && renderSlideLink ? (
      <CardImageCarousel
        images={rawImages}
        resolveAlt={img => resolveAlt(img)}
        imageLoading={imageLoading}
        imageQuality={imageQuality}
        imageMaxWidth={imageMaxWidth}
        renderSlideLink={renderSlideLink}
        onSlideView={onCarouselSlideView}
        ariaLabel={carouselAriaLabel}
      />
    ) : (
      heroImageNode
    )

  const hasTags = Boolean(imageTags)

  return (
    <div
      data-slot="ds-image-tile"
      className={css({
        position: 'relative',
        width: '100%',
        aspectRatio: HERO_MEDIA_ASPECT_RATIO,
        flexShrink: 0,
        isolation: 'isolate',
      })}
    >
      <div
        className={css({
          position: 'absolute',
          inset: 0,
          zIndex: 0,
          overflow: 'hidden',
          '& img': {
            width: '100%',
            height: '100%',
            display: 'block',
          },
        })}
      >
        {imageNode}
      </div>

      {hasTags ? (
        <div
          className={css({
            position: 'absolute',
            bottom: isMini ? 'space-8' : isCompact ? 'space-12' : { base: 'space-8', lg: 'space-12' },
            left: isMini ? 'space-8' : isCompact ? 'space-12' : { base: 'space-8', lg: 'space-12' },
            zIndex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 'space-4',
            alignItems: 'flex-start',
            maxWidth: '100%',
          })}
        >
          {imageTags}
        </div>
      ) : null}
    </div>
  )
}
